/*
 * This File is for intercepting requests using $fetch
*/
import { ofetch } from 'ofetch'
import { Capacitor } from '@capacitor/core'
import { CONFIG } from '~/services/config'
import { spoofQueryParams } from '~/utils/spoof'

/*
 * Services the bearer token may be sent to. The token is a Member Hub credential; it goes
 * to Compare Club's own backends (and the deployed web origin, whose Nitro routes check it)
 * and to nothing else (CLUB-944 T1). Every entry comes from CONFIG so a new first-party
 * service only needs adding there.
 */
const FIRST_PARTY_SERVICES = ['default', 'memberhub', 'web', 'utilities', 'travel', 'tokenization', 'partnerOrder', 'alternativemedia']

const originOf = value => {
  try {
    return new URL(value).origin
  } catch {
    return null
  }
}

const requestUrl = request => {
  if (typeof request === 'string') return request
  if (request instanceof URL) return request.href
  return request?.url || ''
}

/*
 * The origin a request will actually hit: an absolute URL's own origin, else the
 * baseURL's, else the page's (a relative `/api/*` call on the web build).
 */
const targetOrigin = (request, options) => {
  const url = requestUrl(request)
  if (/^https?:\/\//i.test(url)) return originOf(url)
  if (options.baseURL) return originOf(options.baseURL)
  return import.meta.client ? window.location.origin : null
}

export default defineNuxtPlugin(() => {
  const { environment } = useRuntimeConfig().public
  const currentExecutingRequests = ref({})
  const requestQueue = ref({})
  const firstPartyOrigins = new Set(
    FIRST_PARTY_SERVICES.map(type => originOf(CONFIG.baseUrl(environment, type))).filter(Boolean)
  )
  if (import.meta.client) {
    firstPartyOrigins.add(window.location.origin)
  }
  const memberhubOrigin = originOf(CONFIG.baseUrl(environment, 'memberhub'))
  globalThis.$fetch = ofetch.create({
    headers: useRequestHeaders(['cookie']),
    onRequest ({ request, options }) {
      // Only override the baseURL if the apiType is defined
      if (options.apiType) {
        options.baseURL = CONFIG.baseUrl(environment, options.apiType)
      }

      /*
       | ===========================================================================
       | Capacitor (iOS/Android) has no Nitro server bundled with the app, so any
       | relative `/api/*` call would resolve to `capacitor://localhost/api/*` and
       | return the SPA's index.html. Re-route those calls to the deployed Nuxt
       | web origin so server routes (server/api/**) actually execute.
       |
       | Skips when:
       |  - a baseURL is already set (explicit override or `apiType` above)
       |  - request is already an absolute URL (http/https)
       |  - path doesn't start with `/api/`
       | ===========================================================================
       */
      if (
        !options.baseURL
        && Capacitor.isNativePlatform()
        && typeof request === 'string'
        && request.startsWith('/api/')
      ) {
        options.baseURL = CONFIG.baseUrl(environment, 'web')
      }

      let currentUrl = requestUrl(request)
      const target = targetOrigin(request, options)
      const isFirstParty = firstPartyOrigins.has(target)
      const isMemberhubApi = !!memberhubOrigin && target === memberhubOrigin
      //  get access token from local storage for JWT
      const token = getLocal('token') ? JSON.parse(getLocal('token')) : {}
      const userHashId = getLocal('auth_user') ? JSON.parse(getLocal('auth_user')) : {}
      if (isFirstParty && !currentUrl.includes('refresh-token') && !currentUrl.includes('reset-password') && token && token.access_token) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token.access_token}`
        }
      }
      if (isFirstParty && userHashId && userHashId.hash_id && !currentUrl.includes('reset-password')) {
        options.query = { ...options.query }
        /*
         | ===============================================================================
         | Restricting `hash_id` addition for urls which have `signature` as default param
         | ===============================================================================
         */
        if (!options.query?.signature) {
          options.query = {
            ...options.query,
            hash_id: userHashId.hash_id
          }
        }
      }

      /*
       | User spoofing case(s) handler - read-only on the backend (GET/HEAD), see utils/spoof.js.
       | Member Hub is the only service that understands these; nothing else should see them.
       */
      const spoofParams = isMemberhubApi ? spoofQueryParams(getLocal('spoof_email'), getLocal('spoof_email_hash')) : {}
      if (Object.keys(spoofParams).length) {
        options.query = {
          ...options.query,
          ...spoofParams
        }
      }

      // Log request
      // console.log('[fetch request]', request, options)

      // Do duplicate request cancellation if enabled
      if (options.canCancel) {
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }

        if (currentExecutingRequests.value[currentUrl]) {
          const cancelController = currentExecutingRequests.value[currentUrl]
          cancelController.abort() // Cancel the previous request
        }

        // Add the cancel method to the request object
        const cancelController = new AbortController()
        options.signal = cancelController.signal

        // Store the cancel controller for the current URL
        currentExecutingRequests.value[currentUrl] = cancelController
      }
      requestQueue.value[currentUrl] = currentUrl
    },

    // eslint-disable-next-line no-unused-vars
    onRequestError ({ request, options, error }) {
      if (error.name === 'AbortError') {
        // console.log('[fetch request error] Aborted by the user')
      }
      // Log error

      // eslint-disable-next-line no-console
      console.log('[fetch request error]', request, error)
    },

    onResponse ({ request, options, response }) {
      if (response && response.status) {
        // Token refresh is now handled in ApiService with proper retry logic.
        // The interceptor no longer needs to manage refresh — it only handles
        // request setup (auth headers, hash_id, etc.) and cleanup.
      }

      if (options.canCancel) {
        let currentUrl = request
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }
        // Clean up the cancel controller when the request is settled
        delete currentExecutingRequests.value[currentUrl]
        delete requestQueue.value[currentUrl]
      }
    },
    onResponseError ({ request, options }) {
      if (options.canCancel) {
        let currentUrl = request
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }
        // Clean up the cancel controller when the request is settled
        delete currentExecutingRequests.value[currentUrl]
      }
      // // Return the custom error response
    }
  })
})
